import type { CommunicationType } from '../../types/tracker.types';

/**
 * Urgency levels for activity items
 */
export type UrgencyLevel = 'low' | 'medium' | 'high' | 'urgent' | null;

/**
 * Color configuration for each activity type (left border)
 */
export const ACTIVITY_TYPE_COLORS: Record<CommunicationType, string> = {
  email: '#3b82f6',        // blue
  sms_outbox: '#10b981',   // green
  sms_inbox: '#10b981',    // green
  status_update: '#8b5cf6', // purple
  tracker_com: '#f59e0b',   // amber
  com_internes: '#6366f1',  // indigo
  com_post_it: '#ec4899',   // pink
  com_avant_envoyer: '#94a3b8', // slate
  rdv: '#0ea5e9',          // sky blue - appointment
  voip_call: '#0d9488',    // teal - voice call recording
};

/**
 * Direction type for inbound/outbound communications
 */
export type MessageDirection = 'inbound' | 'outbound';

/**
 * Direction colors - Blue for sent, Orange for received
 */
export const DIRECTION_COLORS: Record<MessageDirection, string> = {
  outbound: '#3b82f6', // Blue - sent messages
  inbound: '#f59e0b',  // Orange - received messages
};

/**
 * Short labels for type badges below icon
 */
export const TYPE_BADGE_LABELS: Record<CommunicationType, string> = {
  email: 'Email',
  sms_outbox: 'SMS',
  sms_inbox: 'SMS',
  status_update: 'Statut',
  tracker_com: 'Msg',
  com_internes: 'Interne',
  com_post_it: 'Note',
  com_avant_envoyer: 'Pré-envoi',
  rdv: 'RDV',
  voip_call: 'Appel',
};

/**
 * Urgency color configuration
 */
export const URGENCY_COLORS: Record<string, { background: string; border: string; text: string }> = {
  urgent: { background: '#fef2f2', border: '#fecaca', text: '#dc2626' },
  high: { background: '#fff7ed', border: '#fed7aa', text: '#ea580c' },
  medium: { background: '#fffbeb', border: '#fde68a', text: '#ca8a04' },
  low: { background: '#f0fdf4', border: '#bbf7d0', text: '#16a34a' },
};

/**
 * Icons for each activity type
 */
export const ACTIVITY_TYPE_ICONS: Record<CommunicationType, string> = {
  email: 'envelope',
  sms_outbox: 'paper-airplane',
  sms_inbox: 'device-phone-mobile',
  status_update: 'arrow-path',
  tracker_com: 'chat-bubble-left',
  com_internes: 'chat-bubble-left-right',
  com_post_it: 'document-text',
  com_avant_envoyer: 'document',
  rdv: 'calendar',
};

/**
 * Quick action configuration
 */
export interface QuickAction {
  id: string;
  label: string;
  icon?: string;
  onClick: () => void;
  variant?: 'default' | 'danger';
  disabled?: boolean;
}

/**
 * Attachment for activity items
 */
export interface ActivityAttachment {
  id: string;
  name: string;
  contentType: string;
  size: number;
  isInline?: boolean;
}

/**
 * Main props for ActivityItem component
 */
export interface ActivityItemProps {
  /** The communication type */
  type: CommunicationType;

  /** Unique identifier */
  id: string;

  /** Main title (e.g., "Email reçu — John Doe") */
  title: string;

  /** Secondary meta line (e.g., "Objet: Rapport • Ouvert: 27/12") */
  meta?: string;

  /** Content snippet (max 2 lines in collapsed state) */
  snippet?: string;

  /** Full content for expanded view */
  fullContent?: string;

  /** HTML content for expanded view (emails) */
  htmlContent?: string;

  /** Timestamp of the activity */
  timestamp: Date;

  /** Whether the item is currently expanded */
  isExpanded?: boolean;

  /** Callback when expand/collapse is toggled */
  onToggleExpand?: () => void;

  /** Urgency level */
  urgency?: UrgencyLevel;

  /** Whether the item is marked as done */
  isDone?: boolean;

  /** Who marked it as done */
  doneBy?: string;

  /** When it was marked as done */
  doneAt?: Date;

  /** Done comment */
  doneComment?: string;

  /** Whether this item is highlighted (e.g., in "À faire" list) */
  isHighlighted?: boolean;

  /** Whether this item is unread */
  isUnread?: boolean;

  /** Author of the communication */
  author?: string;

  /** Author ID for edit/delete permissions */
  authorId?: string;

  /** Current user ID for permission checks */
  currentUserId?: string;

  /** Assigned user display name */
  assignedTo?: string;

  /** Assigned user ID (UUID) for current user check */
  assignedToId?: string;

  /** For Léa-generated coms: who accepted (validated) the action plan */
  planValidatedByName?: string;

  /** Message direction (inbound/outbound) for color coding */
  direction?: MessageDirection;

  /** Recipient for outbound SMS (phone number or contact name) */
  recipient?: string;

  /** Attachments */
  attachments?: ActivityAttachment[];

  /** Callback when mark done is clicked */
  onMarkDone?: (id: string, done: boolean, comment?: string) => void;

  /** Callback for edit action (tracker_com only) */
  onEdit?: () => void;

  /** Callback for delete action (tracker_com only) */
  onDelete?: () => void;

  /** Whether the item was edited after creation */
  isEdited?: boolean;

  /** When it was last edited */
  editedAt?: Date;

  /** Callback for view full content (emails) */
  onViewFull?: () => void;

  /** Callback for reallocate (emails) */
  onReallocate?: () => void;

  /** Callback for reply (received emails) */
  onReply?: () => void;

  /** Additional quick actions */
  quickActions?: QuickAction[];

  /** Children for custom expanded content */
  children?: React.ReactNode;

  /** Additional class name */
  className?: string;

  /** VoIP call recording URL — when set, renders an inline <audio>
   *  player below the title (used by type='voip_call'). */
  recordingUrl?: string | null;

  /** VoIP call duration in milliseconds (used by type='voip_call'). */
  durationMs?: number | null;

  /** VoIP caller name (from fm_contacts_affaire match by phone). */
  voipContactName?: string | null;
  /** Caller's role(s) on this affaire (DEMANDEUR, PROPRIETAIRE…). */
  voipContactRoles?: string | null;
  /** Agent who handled the call. */
  voipAgentName?: string | null;
  /** GPT-generated 2-3 sentence summary of the call. */
  voipSummary?: string | null;
  /** Full Whisper transcription. */
  voipTranscription?: string | null;
  /** Set once Whisper+GPT have completed (success or hard fail). */
  voipProcessedAt?: string | null;
}

/**
 * ViewModel for transforming raw data to ActivityItem props
 */
export interface ActivityItemViewModel {
  type: CommunicationType;
  id: string;
  title: string;
  meta?: string;
  snippet?: string;
  fullContent?: string;
  htmlContent?: string;
  timestamp: Date;
  urgency?: UrgencyLevel;
  isDone?: boolean;
  doneBy?: string;
  doneAt?: Date;
  doneComment?: string;
  isHighlighted?: boolean;
  isUnread?: boolean;
  author?: string;
  authorId?: string;
  assignedTo?: string;
  /** Assigned user ID (UUID) for current user check */
  assignedToId?: string;
  /** For Léa-generated coms: who accepted (validated) the action plan */
  planValidatedByName?: string;
  /** Message direction (inbound/outbound) for color coding */
  direction?: MessageDirection;
  attachments?: ActivityAttachment[];
  /** Recipient for outbound SMS (phone number or contact name) */
  recipient?: string;
  /** Optional raw data for callbacks */
  rawData?: unknown;

  /** Whether the item was edited after creation */
  isEdited?: boolean;

  /** When it was last edited */
  editedAt?: Date;

  /** VoIP call recording URL (only for type='voip_call'). */
  recordingUrl?: string | null;

  /** VoIP call duration in milliseconds (only for type='voip_call'). */
  durationMs?: number | null;

  voipContactName?: string | null;
  voipContactRoles?: string | null;
  voipAgentName?: string | null;
  voipSummary?: string | null;
  voipTranscription?: string | null;
  voipProcessedAt?: string | null;
}
